import {
  FILTER_OPERATOR_ARITY,
  type AnalysisDimension,
  type AnalysisMeasure,
  type AnalysisState,
  type Filter,
  type FilterOperator,
  type FilterTarget,
  type ScalarValue,
  type Selection,
  type TimeGrain,
} from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import type { FieldCatalog, FieldKind } from './field-catalog';
import { accept, reject, type StepResult } from './rejection';

/** Contexto de solo lectura con el que se validan los comandos. */
export interface CommandContext {
  readonly index: SemanticModelIndex;
  readonly catalog: FieldCatalog;
}

const MAX_FIELDS_PER_KIND = 12;

/** Clave local valida (`FieldKeySchema`) y unica derivada del id del objeto del modelo. */
export function allocateFieldKey(objectId: string, taken: ReadonlySet<string>): string {
  const tail = objectId.slice(objectId.lastIndexOf('.') + 1);
  let base = tail.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 56);
  if (!/^[A-Za-z_]/.test(base)) base = `f_${base}`;
  let candidate = base;
  for (let ordinal = 2; taken.has(candidate); ordinal++) candidate = `${base}_${String(ordinal)}`;
  return candidate;
}

const fieldKeys = (state: AnalysisState): Set<string> =>
  new Set([...state.dimensions.map((d) => d.key), ...state.measures.map((m) => m.key)]);

export interface AddFieldInput {
  readonly fieldId: string;
  /** Posicion de insercion dentro de su lista; por defecto al final. */
  readonly position?: number;
  /** Solo para la zona de destino: rechaza si el campo no es de este tipo. */
  readonly expectedKind?: FieldKind;
}

export function addField(
  state: AnalysisState,
  context: CommandContext,
  input: AddFieldInput,
): StepResult {
  const field = context.catalog.get(input.fieldId);
  if (!field) return reject('unknown-field', 'El campo no existe en el modelo.');
  if (input.expectedKind !== undefined && field.kind !== input.expectedKind) {
    return reject(
      'kind-mismatch',
      field.kind === 'measure'
        ? `"${field.name}" es una medida y no puede usarse como dimension.`
        : `"${field.name}" es una dimension y no puede usarse como medida.`,
    );
  }
  const taken = fieldKeys(state);
  if (field.kind === 'dimension') {
    if (state.dimensions.some((d) => d.dimensionId === field.id)) {
      return reject('duplicate-field', `"${field.name}" ya forma parte del analisis.`);
    }
    if (state.dimensions.length >= MAX_FIELDS_PER_KIND) {
      return reject('limit-reached', 'Se alcanzo el maximo de dimensiones por visualizacion.');
    }
    const dimension: AnalysisDimension = {
      key: allocateFieldKey(field.id, taken),
      dimensionId: field.id,
      ...(field.temporal && field.defaultTimeGrain !== null
        ? { timeGrain: field.defaultTimeGrain }
        : {}),
    };
    return accept({ ...state, dimensions: insertAt(state.dimensions, dimension, input.position) });
  }
  if (state.measures.some((m) => m.measureId === field.id)) {
    return reject('duplicate-field', `"${field.name}" ya forma parte del analisis.`);
  }
  if (state.measures.length >= MAX_FIELDS_PER_KIND) {
    return reject('limit-reached', 'Se alcanzo el maximo de medidas por visualizacion.');
  }
  const measure: AnalysisMeasure = { key: allocateFieldKey(field.id, taken), measureId: field.id };
  return accept({ ...state, measures: insertAt(state.measures, measure, input.position) });
}

/** Quita un campo; si era una dimension con drilldown activo, el drilldown tambien se descarta. */
export function removeField(
  state: AnalysisState,
  context: CommandContext,
  key: string,
): StepResult {
  const dimensions = state.dimensions.filter((d) => d.key !== key);
  const measures = state.measures.filter((m) => m.key !== key);
  if (dimensions.length === state.dimensions.length && measures.length === state.measures.length) {
    return reject('unknown-field', 'El campo no forma parte del analisis.');
  }
  const removed = state.dimensions.find((d) => d.key === key);
  const drilldown =
    removed === undefined
      ? state.drilldown
      : state.drilldown.filter(
          (drill) =>
            !context.index
              .getHierarchy(drill.hierarchyId)
              ?.levels.some((level) => level.dimensionId === removed.dimensionId),
        );
  return accept({ ...state, dimensions, measures, drilldown });
}

export function moveField(
  state: AnalysisState,
  kind: FieldKind,
  from: number,
  to: number,
): StepResult {
  const list = kind === 'dimension' ? state.dimensions : state.measures;
  if (from < 0 || from >= list.length || to < 0 || to >= list.length) {
    return reject('invalid-move', 'Posicion de destino fuera de rango.');
  }
  if (from === to) return accept(state);
  return kind === 'dimension'
    ? accept({ ...state, dimensions: reorder(state.dimensions, from, to) })
    : accept({ ...state, measures: reorder(state.measures, from, to) });
}

export function setTimeGrain(
  state: AnalysisState,
  context: CommandContext,
  key: string,
  grain: TimeGrain | null,
): StepResult {
  const position = state.dimensions.findIndex((d) => d.key === key);
  const current = state.dimensions[position];
  if (!current) return reject('unknown-field', 'La dimension no forma parte del analisis.');
  if (grain !== null && context.catalog.get(current.dimensionId)?.temporal !== true) {
    return reject('invalid-state', 'Solo las dimensiones temporales admiten granularidad.');
  }
  const next: AnalysisDimension = { key: current.key, dimensionId: current.dimensionId };
  if (current.label !== undefined) next.label = current.label;
  if (grain !== null) next.timeGrain = grain;
  const dimensions = [...state.dimensions];
  dimensions[position] = next;
  return accept({ ...state, dimensions });
}

export interface FilterDraft {
  readonly target: FilterTarget;
  readonly operator: FilterOperator;
  readonly values: readonly ScalarValue[];
}

function checkFilter(context: CommandContext, draft: FilterDraft): string | null {
  const targetId =
    draft.target.kind === 'dimension' ? draft.target.dimensionId : draft.target.measureId;
  const field = context.catalog.get(targetId);
  if (field?.kind !== draft.target.kind) return 'El filtro apunta a un campo inexistente.';
  const [min, max] = FILTER_OPERATOR_ARITY[draft.operator];
  if (draft.values.length < min || draft.values.length > max) {
    return `El operador "${draft.operator}" no admite ${String(draft.values.length)} valor(es).`;
  }
  return null;
}

/** Id determinista para un filtro nuevo: `fN` con N mayor que cualquier existente. */
export function nextFilterId(filters: readonly Filter[]): string {
  let max = 0;
  for (const filter of filters) {
    const match = /^f(\d+)$/.exec(filter.id);
    if (match?.[1] !== undefined) max = Math.max(max, Number(match[1]));
  }
  return `f${String(max + 1)}`;
}

export function addFilter(
  state: AnalysisState,
  context: CommandContext,
  draft: FilterDraft,
): StepResult {
  const problem = checkFilter(context, draft);
  if (problem !== null) return reject('invalid-filter', problem);
  const filter: Filter = {
    id: nextFilterId(state.filters),
    target: draft.target,
    operator: draft.operator,
    values: [...draft.values],
  };
  return accept({ ...state, filters: [...state.filters, filter] });
}

export function updateFilter(
  state: AnalysisState,
  context: CommandContext,
  filterId: string,
  change: Pick<FilterDraft, 'operator' | 'values'>,
): StepResult {
  const current = state.filters.find((filter) => filter.id === filterId);
  if (!current) return reject('invalid-filter', 'El filtro no existe.');
  const draft: FilterDraft = { target: current.target, ...change };
  const problem = checkFilter(context, draft);
  if (problem !== null) return reject('invalid-filter', problem);
  return accept({
    ...state,
    filters: state.filters.map((filter) =>
      filter.id === filterId
        ? { ...filter, operator: change.operator, values: [...change.values] }
        : filter,
    ),
  });
}

export function removeFilter(state: AnalysisState, filterId: string): StepResult {
  const filters = state.filters.filter((filter) => filter.id !== filterId);
  if (filters.length === state.filters.length) {
    return reject('invalid-filter', 'El filtro no existe.');
  }
  return accept({ ...state, filters });
}

/** Orden por un unico campo (o sin orden explicito con `direction = null`). */
export function setSort(
  state: AnalysisState,
  key: string,
  direction: 'asc' | 'desc' | null,
): StepResult {
  if (!fieldKeys(state).has(key)) return reject('unknown-field', 'Clave de orden inexistente.');
  return accept({ ...state, sorting: direction === null ? [] : [{ key, direction }] });
}

export function setGrouping(
  state: AnalysisState,
  change: { readonly grandTotal?: boolean; readonly subtotals?: boolean },
): StepResult {
  const grandTotal = change.grandTotal ?? state.grouping.grandTotal;
  const subtotals = change.subtotals ?? state.grouping.subtotals;
  const firstKey = state.dimensions[0]?.key;
  const rollupKeys = subtotals && firstKey !== undefined ? [firstKey] : [];
  return accept({ ...state, grouping: { grandTotal, subtotals, rollupKeys } });
}

const sameValue = (a: ScalarValue, b: ScalarValue): boolean => a === b;

/**
 * Alterna un valor seleccionado de una dimension. En modo `single` (o sin `additive`)
 * reemplaza la seleccion de esa dimension; en modo multiple con `additive` la amplia.
 * Volver a elegir el unico valor seleccionado lo deselecciona.
 */
export function toggleSelection(
  selections: readonly Selection[],
  input: {
    readonly dimensionId: string;
    readonly value: ScalarValue;
    readonly additive: boolean;
    readonly mode: 'single' | 'multiple';
  },
): Selection[] {
  const current = selections.find((selection) => selection.dimensionId === input.dimensionId);
  const others = selections.filter((selection) => selection.dimensionId !== input.dimensionId);
  const values = current?.values ?? [];
  const isSelected = values.some((value) => sameValue(value, input.value));
  let nextValues: ScalarValue[];
  if (input.mode === 'multiple' && input.additive) {
    nextValues = isSelected
      ? values.filter((value) => !sameValue(value, input.value))
      : [...values, input.value];
  } else {
    nextValues = isSelected && values.length === 1 ? [] : [input.value];
  }
  return nextValues.length === 0
    ? others
    : [...others, { dimensionId: input.dimensionId, values: nextValues }];
}

export function clearSelections(
  selections: readonly Selection[],
  dimensionId?: string,
): Selection[] {
  return dimensionId === undefined
    ? []
    : selections.filter((selection) => selection.dimensionId !== dimensionId);
}

/** Baja un nivel en la jerarquia de la dimension `key`, fijando `value` en el nivel actual. */
export function drillDown(
  state: AnalysisState,
  context: CommandContext,
  key: string,
  value: ScalarValue,
): StepResult {
  const dimension = state.dimensions.find((d) => d.key === key);
  if (!dimension) return reject('unknown-field', 'La dimension no forma parte del analisis.');
  const located = locateLevel(state, context, dimension.dimensionId);
  if (!located) return reject('no-hierarchy', 'La dimension no pertenece a una jerarquia.');
  const { hierarchy, depth, drill } = located;
  const current = hierarchy.levels[depth];
  const next = hierarchy.levels[depth + 1];
  if (!current || !next) return reject('no-hierarchy', 'No hay un nivel inferior.');
  const path = [...(drill?.path ?? []), { levelId: current.id, value }];
  const entry = { hierarchyId: hierarchy.id, levelId: next.id, path };
  return accept({
    ...state,
    drilldown: [...state.drilldown.filter((d) => d.hierarchyId !== hierarchy.id), entry],
  });
}

/** Sube un nivel en la jerarquia; al llegar al nivel original elimina el drilldown. */
export function drillUp(state: AnalysisState, hierarchyId: string): StepResult {
  const drill = state.drilldown.find((d) => d.hierarchyId === hierarchyId);
  if (!drill) return reject('no-hierarchy', 'No hay drilldown activo en esa jerarquia.');
  const last = drill.path.at(-1);
  const rest = state.drilldown.filter((d) => d.hierarchyId !== hierarchyId);
  if (!last || drill.path.length === 1) return accept({ ...state, drilldown: rest });
  return accept({
    ...state,
    drilldown: [...rest, { hierarchyId, levelId: last.levelId, path: drill.path.slice(0, -1) }],
  });
}

/** Nivel efectivo de una dimension del estado (considerando un drilldown activo). */
function locateLevel(state: AnalysisState, context: CommandContext, dimensionId: string) {
  for (const hierarchy of context.index.model.hierarchies) {
    const baseDepth = hierarchy.levels.findIndex((level) => level.dimensionId === dimensionId);
    if (baseDepth < 0) continue;
    const drill = state.drilldown.find((d) => d.hierarchyId === hierarchy.id);
    const depth =
      drill === undefined
        ? baseDepth
        : hierarchy.levels.findIndex((level) => level.id === drill.levelId);
    return { hierarchy, depth, drill };
  }
  return undefined;
}

function insertAt<T>(list: readonly T[], item: T, position: number | undefined): T[] {
  const index = position === undefined ? list.length : Math.max(0, Math.min(position, list.length));
  return [...list.slice(0, index), item, ...list.slice(index)];
}

function reorder<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}
